import axios, { AxiosError } from 'axios';

/**
 * Activity Service
 *
 * Handles all activity-related API requests to the backend.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Activity endpoints
const ACTIVITY_ENDPOINTS = {
  system: `${API_BASE_URL}/activities/system`,
  company: `${API_BASE_URL}/activities/company`,
  project: (projectId: string) => `${API_BASE_URL}/activities/project/${projectId}`,
  my: `${API_BASE_URL}/activities/my`
};

/**
 * Activity interface matching backend
 */
export interface Activity {
  id: string;
  company_id: string | null;
  project_id: string | null;
  requirement_id: string | null;
  user_id: string;
  action_type: string;
  description: string;
  metadata?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
  created_at: string;
  // Joined fields
  user_first_name?: string;
  user_last_name?: string;
  user_email?: string;
  company_name?: string;
  project_name?: string;
  project_code?: string;
}

/**
 * API Response interfaces
 */
export interface ActivityListResponse {
  success: boolean;
  data: {
    activities: Activity[];
    total: number;
  };
}

export interface APIError {
  error: string;
  details?: string;
}

/**
 * Handle API errors
 */
function handleAPIError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>;
    const errorMessage = axiosError.response?.data?.error || axiosError.message;
    throw new Error(errorMessage);
  }
  throw error;
}

/**
 * Get activities for the current user's company
 */
export async function getCompanyActivities(
  params: {
    limit?: number;
    offset?: number;
    action_type?: string;
    project_id?: string;
  },
  token: string
): Promise<Activity[]> {
  try {
    const response = await axios.get<ActivityListResponse>(ACTIVITY_ENDPOINTS.company, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params
    });
    return response.data.data.activities;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get activities for a specific project
 */
export async function getProjectActivities(
  projectId: string,
  params: {
    limit?: number;
    offset?: number;
  },
  token: string
): Promise<Activity[]> {
  try {
    const response = await axios.get<ActivityListResponse>(
      ACTIVITY_ENDPOINTS.project(projectId),
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      }
    );
    return response.data.data.activities;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get activities for the current user
 */
export async function getMyActivities(
  params: {
    limit?: number;
    offset?: number;
  },
  token: string
): Promise<Activity[]> {
  try {
    const response = await axios.get<ActivityListResponse>(ACTIVITY_ENDPOINTS.my, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params
    });
    return response.data.data.activities;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get system-wide activities across all companies (Platform Admin only)
 */
export async function getSystemActivities(
  params: {
    limit?: number;
    offset?: number;
  },
  token: string
): Promise<Activity[]> {
  try {
    const response = await axios.get<ActivityListResponse>(ACTIVITY_ENDPOINTS.system, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params
    });
    return response.data.data.activities;
  } catch (error) {
    handleAPIError(error);
  }
}

export default {
  getCompanyActivities,
  getProjectActivities,
  getMyActivities,
  getSystemActivities
};
